import { newsList } from "@/mocks/news";
import { categories } from "@/mocks/categories";
import { topics } from "@/mocks/topics";
import type { NewsItem, Category, Topic } from "@/types/news";

export const allNews: NewsItem[] = newsList as NewsItem[];
export const allCategories: Category[] = categories as Category[];
export const allTopics: Topic[] = topics as Topic[];

export function toTimestamp(value: string): number {
  return new Date(value.replace(/-/g, "/")).getTime();
}

export function sortByTimeDesc(list: NewsItem[]): NewsItem[] {
  return [...list].sort(
    (a, b) => toTimestamp(b.publishedAt) - toTimestamp(a.publishedAt)
  );
}

export function sortByTimeAsc(list: NewsItem[]): NewsItem[] {
  return [...list].sort(
    (a, b) => toTimestamp(a.publishedAt) - toTimestamp(b.publishedAt)
  );
}

export function getCategory(slug: string): Category | undefined {
  return allCategories.find((item) => item.slug === slug);
}

export function getCategoryName(slug: string): string {
  return getCategory(slug)?.name ?? slug;
}

export function getTopic(id: string): Topic | undefined {
  return allTopics.find((item) => item.id === id);
}

export function getTopicName(id: string): string {
  return getTopic(id)?.name ?? id;
}

export function getNewsById(id: string): NewsItem | undefined {
  return allNews.find((item) => item.id === id);
}

export function getNewsByCategory(slug: string): NewsItem[] {
  if (slug === "exclusive") {
    return allNews.filter((item) => item.exclusive);
  }
  return allNews.filter((item) => item.category === slug);
}

export function getNewsByTopic(topicId: string): NewsItem[] {
  return allNews.filter((item) => item.topics.includes(topicId));
}

export function getFocusNews(): NewsItem[] {
  return sortByTimeDesc(allNews.filter((item) => item.isFocus));
}

export function getLatestNews(limit = 6): NewsItem[] {
  return sortByTimeDesc(allNews).slice(0, limit);
}

export function getExclusiveNews(limit = 4): NewsItem[] {
  return sortByTimeDesc(allNews.filter((item) => item.exclusive)).slice(
    0,
    limit
  );
}

export function getRelatedNews(current: NewsItem, limit = 3): NewsItem[] {
  const scored = allNews
    .filter((item) => item.id !== current.id)
    .map((item) => {
      const sharedTopics = item.topics.filter((t) =>
        current.topics.includes(t)
      ).length;
      const sameCategory = item.category === current.category ? 1 : 0;
      let score = sharedTopics * 3 + sameCategory * 2;
      if (item.exclusive === current.exclusive && current.exclusive) {
        score += 1;
      }
      return { item, score };
    })
    .sort(
      (a, b) =>
        b.score - a.score || toTimestamp(b.item.publishedAt) - toTimestamp(a.item.publishedAt)
    );
  return scored.slice(0, limit).map((entry) => entry.item);
}

export function searchNews(keyword: string): NewsItem[] {
  const word = keyword.trim().toLowerCase();
  if (!word) {
    return [];
  }
  const matches = allNews.filter((item) => {
    const body = item.content
      .map((block) => block.text ?? block.caption ?? "")
      .join(" ");
    const haystack = [
      item.title,
      item.summary,
      item.source,
      item.author,
      getCategoryName(item.category),
      body,
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(word);
  });
  return sortByTimeDesc(matches);
}

export function formatViews(views: number): string {
  if (views >= 10000) {
    return `${(views / 10000).toFixed(1)}万`;
  }
  return String(views);
}

export function getPlainText(item: NewsItem): string {
  return item.content
    .map((block) => block.text ?? "")
    .filter(Boolean)
    .join("");
}